import { useTranslation } from 'react-i18next';
import {
  Box,
  PasswordInput,
  PasswordInputProps,
  Progress,
  Popover,
  Text,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useState, useMemo } from 'react';

function PasswordRequirement({
  meets,
  label,
}: {
  meets: boolean;
  label: string;
}) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      {meets ? <IconCheck size={14} /> : <IconX size={14} />}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

function getStrength(password: string, requirements: { re: RegExp }[]) {
  let multiplier = password.length > 5 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

export default function PasswordInputWithRequirements(
  props: PasswordInputProps,
) {
  const { t } = useTranslation();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [value, setValue] = useState(props.defaultValue?.toString() ?? '');

  const requirements = useMemo(
    () => [
      { re: /[0-9]/, label: t('common.passwordRequirements.number') },
      {
        re: /[a-z]/,
        label: t('common.passwordRequirements.lowercase'),
      },
      {
        re: /[A-Z]/,
        label: t('common.passwordRequirements.uppercase'),
      },
      {
        re: /[$&+,:;=?@#|'<>.^*()%!_/\\-]/,
        label: t('common.passwordRequirements.special'),
      },
    ],
    [t],
  );

  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(value)}
    />
  ));

  const strength = getStrength(value, requirements);

  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  return (
    <Popover
      opened={popoverOpened}
      position="bottom"
      width="target"
      transitionProps={{ transition: 'pop' }}
    >
      <Popover.Target>
        <div
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
        >
          <PasswordInput
            {...props}
            onChange={(event) => {
              setValue(event.currentTarget.value);
              props.onChange?.(event);
            }}
          />
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Progress color={color} value={strength} size={5} mb="xs" />
        <PasswordRequirement
          label={t('common.passwordRequirements.minLength')}
          meets={value.length > 5}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  );
}
